import gsap from "../vendor/gsap/gsap.min";
import "../vendor/gsap/ScrollTrigger.min";
import "../vendor/gsap/ScrollToPlugin.min";
import Observer from "../vendor/gsap/Observer.min";
import ScrollSmoother from "../vendor/gsap/ScrollSmoother.min";
import mapboxgl from "mapbox-gl";
import Plyr from "plyr";

import LocationSearch from "./location_search";
import { defineCustomElements } from 'vidstack/elements';
import * as Sentry from "@sentry/browser";

mapboxgl.accessToken = "pk.eyJ1IjoicW9udm95IiwiYSI6ImNsbDBvMmVqczAxbjIzZXBkcnBkZG5yYmIifQ.eU1tylIQhAWyRRSa1aHGjw";
gsap.defaults({ duration: 1, ease: "none" });

const Hooks = {};

Hooks.PlyrPlayer = {
  mounted() {
    let options = {
      autoplay: true,
      muted: false,
      youtube: { noCookie: true },
      storage: { enabled: false },
      fullscreen: { iosNative: true }
    }

    new Plyr(this.el, options);
  }
}

Hooks.HeaderBlock = {
  mounted() {
    this.observer = Observer.create({
      type: "scroll",
      onDown: () => {
        this.el.classList.add("scrolled");

      },
      onChangeY: (self) => {
        if (self.deltaY < 0) {
          if (window.scrollY <= 5) {
            this.el.classList.remove("scrolled");
          }
        }
      }
    });

    if (window.scrollY > 5) {
      this.el.classList.add("scrolled");
    }
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    if (this.observer) {
      this.observer.kill();
    }
  }
}
Hooks.IntroBlockAnimation = {
  mounted() {
    const block = gsap.utils.selector(this.el);
    const triggerBlock = this.el.nextElementSibling;
    const scrollIconEl = document.querySelector(".scroll");

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerBlock,
        start: "top 95%",
        end: "top 10%",
        scrub: true,
      }
    });

    tl.to(block(".text"), { autoAlpha: 0, y: "-=40px" })
      .to(block(".bg img, .bg .before, .bg .after"), { autoAlpha: 0 }, "<")

    if (scrollIconEl) {
      tl.to(scrollIconEl, { opacity: 0 });
    }

    this.tl = tl;
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    this.tl.kill();
  }
}

Hooks.HomeSlidesBlockAnimation = {
  mounted() {
    this.scrollOffset = "140px";
    this.match_media = gsap.matchMedia();
    this.match_media.add("(max-width: 1100px)", () => {
      this.scrollOffset = "120px";
    });
    this.match_media.add("(max-width: 767px)", () => {
      this.scrollOffset = "90px";
    });
    this.match_media.add("(min-width: 1101px)", () => {
      this.scrollOffset = "140px";
    });

    const prevBlock = gsap.utils.selector(this.el.previousElementSibling);
    const block = gsap.utils.selector(this.el);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.el,
        start: "top 95%",
        end: "top top-=200px",
        scrub: true,
        onEnter: () => {
          document.querySelector("#header").classList.remove("start");
        },
        onLeaveBack: () => {
          document.querySelector("#header").classList.add("start");
        }
      }
    });
    this.tl = tl;

    if (ScrollTrigger.isTouch === 1) {
      tl.to(prevBlock(".bg"), { height: "calc(100% - 100px)" }, "<+=50%")  // Move the background of the first slide up
    }
    else {
      tl.fromTo(block(".slides .slide:first-child"), { scale: 0.5 }, { scale: 1 })
        .to(prevBlock(".bg"), { height: "calc(100% - 100px)" }, "<+=50%")  // Move the background of the first slide up
    }

    const slides = gsap.utils.toArray(".slide", this.el)
    this.slideTimelines = [];

    defineCustomElements();

    function playVideo(item) {
      const video = item.querySelector("media-player");
      if (!video) return;

      video.addEventListener("autoplay-fail", () => {
        console.log("Autoplay fail")
      });

      window.customElements.whenDefined("media-player").then(() => {
        video.onAttach(() => {
          video.currentTime = 0;

          video.play().catch(error => {
            if (error.name === "NotAllowedError") {
              console.log("Cannot play video because it was not allowed.")
            }

            video.addEventListener("can-play", () => {
              video.play().catch(error => {
                if (error.name === "NotAllowedError") {
                  console.log("Cannot play video because it was not allowed.")
                }
              });
            });
          });
        });
      });
    }

    slides.forEach((item, index) => {
      const slide = gsap.utils.selector(item);
      const slideTl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: "top 75%",
          end: "bottom 25%",
          scrub: true,
          onEnter() {
            playVideo(item);
          },
          onLeave() {
            if (index == slides.length - 1) {
              document.querySelector("#header").classList.add("scrolled");
            }
          },
          onEnterBack() {
            playVideo(item);
            document.querySelector("#header").classList.remove("scrolled");
          }
        }
      });

      slideTl.fromTo(slide(".text")[0], { autoAlpha: 0 }, { autoAlpha: 1 })
        .to(slide(".text")[0], { autoAlpha: 0 });

      // Fade out .bg after last slide
      if (index == (slides.length - 1)) {
        const lastTl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            scrub: true,
            start: "bottom bottom",
            end: "bottom top"
          }
        })
        lastTl.to(prevBlock(".bg"), { height: 0 });
        this.slideTimelines.push(lastTl);
      }

      this.slideTimelines.push(slideTl);
    });
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  preventTouch(e) {
    if (this.isScrolling) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  },

  destroyed() {
    this.tl.kill();
    this.slideTimelines.forEach(tl => tl.kill());
  },

  isInView() {
    const rect = this.snaps.at(-1).getBoundingClientRect();
    return (rect.top) > 0;
  },

  whereAmI() {
    let position = -1;

    for (i = 0; i < this.snaps.length; i++) {
      const rect = this.snaps.at(i).getBoundingClientRect();

      if (rect.top > 0 && rect.top < window.innerHeight) {
        position = i;
        break;
      }
    }

    return position;
  }
}

function scrollTo() {
  try {
    if (document.location.hash !== "" && document.querySelector(document.location.hash)) {
      const headerEl = document.querySelector("#header")

      if (headerEl) {
        const headerHeight = headerEl.offsetHeight;
        const smoother = ScrollSmoother.get();

        if (smoother) {
          smoother.scrollTo(document.location.hash, true, `top ${headerHeight}px`);
        }
        else {
          gsap.to(window, { scrollTo: { y: document.location.hash, offsetY: headerHeight } })
        }
      }
    }
  }
  catch { }
}

Hooks.SmoothScroll = {
  mounted() {
    const isHome = this.el.dataset.isHome !== undefined;

    if (isHome) {
      this.ctx = gsap.context(() => {
        const smoother = ScrollSmoother.create({
          effects: true,
          wrapper: "[data-phx-main]",
          normalizeScroll: (ScrollTrigger.isTouch === 1) && isHome,
        });
        smoother.effects(".slide-mask img", { speed: "0.01" });
      });
    }

    gsap.delayedCall(0.2, () => scrollTo());
  },

  destroyed() {
    if (this.ctx) {
      try {
        this.ctx.revert();
      }
      catch (err) {
        Sentry.captureException(err);
        location.reload();
      }
    }
  }
}

function standard_timeline(hook) {
  let top = 95;
  const mm = gsap.matchMedia();

  mm.add("(min-width: 768px)", () => {
    top = 95;
  });
  mm.add("(max-width: 767px)", () => {
    top = 100;
  });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: hook.el,
      start: () => `top ${top}%`,
      end: "top 70%",
      scrub: true,
      once: true
    }
  });
  tl.fromTo(hook.el, { y: 100 }, { y: 0 });

  return tl;
}

function flip_on_hover(items, block_element) {
  return gsap.context(() => {
    items.forEach(item => {
      Observer.create({
        target: item,
        type: "pointer",
        onHover: function(self) {
          const half = window.innerHeight / 2;

          block_element.style.transform = "none";
          if (self.event.clientY < half) {
            item.classList.remove("flip-up");
          }
          else {
            item.classList.add("flip-up");
          }
        }
      });
    });
  })
}

Hooks.StandardBlock = {
  mounted() {
    this.tl = standard_timeline(this);
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    if (this.tl) {
      this.tl.kill();
    }
  }
}

Hooks.TeamBlock = {
  mounted() {
    this.tl = standard_timeline(this);
    const items = gsap.utils.toArray(".image", this.el);
    this.context = flip_on_hover(items, this.el);
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    this.context.kill();
    this.tl.kill()
  }
}

Hooks.PartnerBlock = {
  mounted() {
    this.tl = standard_timeline(this);

    const items = gsap.utils.toArray(".listitem", this.el);
    this.context = flip_on_hover(items, this.el);
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    this.context.kill();
    this.tl.kill()
  }
}

Hooks.IntroBlock = {
  mounted() {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.el,
        start: "top top",
        end: "bottom 40%",
        scrub: true
      }
    });
    tl.to(this.el.querySelectorAll(".text, .breadcrumbs"), { autoAlpha: 0 });

    this.tl = tl;
    // ScrollSmoother.get().scrollTo(0);
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    this.tl.kill()
  }
}

Hooks.LargeImageOrVideoBlock = {
  mounted() {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.el,
        start: "top 95%",
        end: "bottom 5%",
        scrub: true
      }
    });
    tl.fromTo(this.el.querySelector(".media"), { scale: 0.3 }, { scale: 1 })
      .to(this.el.querySelector(".media"), { scale: 0.75 });

    this.tl = tl;
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    this.tl.kill()
  }
}

Hooks.ScrollToNextBlock = {
  mounted() {
    this.el.addEventListener("click", () => {
      const nextBlock = this.el.closest(".block").nextElementSibling;
      let offset = 0;

      if (this.el.dataset.scrollOffset) {
        offset = 0;
      } else {
        const header = document.querySelector(".header");
        offset = header.offsetHeight;
      }
      gsap.to(window, { duration: 1, scrollTo: { y: nextBlock, offsetY: offset } });
    });
  }
}

Hooks.LocalNumber = {
  mounted() {
    this.updated();
  },

  updated() {
    const text = this.el.textContent.match(/[\d\.]+/)
    let number = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(text);
    this.el.textContent = number;
  }
}

Hooks.LocalTime = {
  mounted() {
    this.updated()
  },

  updated() {
    // options: full, long, medium, short
    const locale = this.el.dataset.locale;

    const time_style = this.el.dataset.timeStyle || "short";
    const date_style = this.el.dataset.dateStyle || "full";

    const template = this.el.querySelector("template")
    const content = template.content.textContent;
    const date = new Date(content.trim());
    const formatter = new Intl.DateTimeFormat(locale, {
      timeStyle: (time_style == "none" ? undefined : time_style),
      dateStyle: (date_style == "none" ? undefined : date_style)
    });

    try {
      template.replaceWith(formatter.format(date));
    }
    catch (e) {
      if (e instanceof RangeError) {
        this.el.textContent = "";
      }
    }
  }
}

Hooks.Map = {
  mounted() {
    const coords = this.el.dataset.coords
      .replaceAll(" ", "")
      .trim()
      .split(",")

    coords[0] = Number(coords[0]).toFixed(4);
    coords[1] = Number(coords[1]).toFixed(4)

    const zoom = 13;
    const pin_color = "E44A32";

    const map_element = this.el;
    const image = new Image();
    image.src = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l+${pin_color}(${coords[1]},${coords[0]})/${coords[1]},${coords[0]},${zoom},0/1280x330@2x?access_token=${mapboxgl.accessToken}`;

    map_element.appendChild(image);

    image.addEventListener("click", () => {
      image.remove();
      plausible("ClickMap");

      this.map = new mapboxgl.Map({
        container: this.el,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [coords[1], coords[0]],
        zoom: zoom,
        cooperativeGestures: true,
        // dragPan: false,
        dragRotate: false,
        attributionControl: true,
        // scrollZoom: false,
        touchPitch: false
      });

      const popup = new mapboxgl.Popup({ offset: 25 }).setText(this.el.dataset.name);

      new mapboxgl.Marker({ color: `#${pin_color}` })
        .setLngLat([coords[1], coords[0]])
        .setPopup(popup)
        .addTo(this.map);
    });
  },

  updated() {
    this.destroyed();
    this.mounted();
  },

  destroyed() {
    if (this.map) {
      this.map.remove();
    }
  }

}

Hooks.LocationSearch = {
  mounted() {
    new LocationSearch(this)
  }
}

Hooks.EventDate = {
  mounted() {
    this.updated();
  },

  checker(x) {
    if (x > 3 && x < 21) return "th";
    switch (x % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  },

  updated() {
    // options: full, long, medium, short
    const content = this.el.dataset.datetime;
    if (!content) { return; }

    const date = new Date(content.trim());
    const formatter = new Intl.DateTimeFormat(undefined, {
      dateStyle: "full"
    });

    try {
      const parts = formatter.formatToParts(date)
      let dateParts = {};
      parts.map(({ type, value }) => {
        dateParts[type] = value;
      });

      const dayEl = this.el.querySelector(".day");
      const monthEl = this.el.querySelector(".month");

      dayEl.innerHTML = `${dateParts["day"]}<span>${this.checker(dateParts["day"])}</span>`;
      monthEl.textContent = `${monthEl.dataset.monthPrefix} ${dateParts["month"]}`;
    }
    catch { }
  }
}

export default Hooks;
